import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, LayoutGrid, Settings, LineChart, Gauge, User, Wallet } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useOutlets } from "@/lib/outlets";
import Logo from "@/components/Logo";
import OutletSwitcher from "@/components/OutletSwitcher";

const ROLE_COLORS = {
  owner: "bg-primary text-primary-foreground",
  cashier: "bg-[hsl(var(--brand-plate))] text-primary-foreground",
  viewer: "bg-muted text-muted-foreground",
};

export default function Layout() {
  const { lock, role, userName, can } = useAuth();
  const { outlet } = useOutlets();
  const nav = useNavigate();

  const doLock = () => {
    lock();
    nav("/lock", { replace: true });
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 lg:px-4 py-2 text-xs font-heading uppercase tracking-[0.12em] transition-colors ${
      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
    }`;

  return (
    <div className="h-screen w-full flex flex-col bg-background text-foreground overflow-hidden">
      <header className="h-16 border-b border-border flex items-center justify-between px-4 lg:px-6 bg-card shrink-0 gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <Logo size="sm" />
          <OutletSwitcher />
        </div>

        <nav className="flex items-center gap-1">
          {can("dashboard") && (
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
              <Gauge strokeWidth={1.5} className="w-4 h-4" /> <span className="hidden lg:inline">Dashboard</span>
            </NavLink>
          )}
          {can("pos") && (
            <NavLink to="/" end className={linkCls} data-testid="nav-pos">
              <LayoutGrid strokeWidth={1.5} className="w-4 h-4" /> <span className="hidden lg:inline">Terminal</span>
            </NavLink>
          )}
          {role === "owner" && (
            <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
              <Settings strokeWidth={1.5} className="w-4 h-4" /> <span className="hidden lg:inline">Admin</span>
            </NavLink>
          )}
          {can("reports") && (
            <NavLink to="/reports" className={linkCls} data-testid="nav-reports">
              <LineChart strokeWidth={1.5} className="w-4 h-4" /> <span className="hidden lg:inline">Reports</span>
            </NavLink>
          )}
          {can("expenses") && (
            <NavLink to="/expenses" className={linkCls} data-testid="nav-expenses">
              <Wallet strokeWidth={1.5} className="w-4 h-4" /> <span className="hidden lg:inline">Expenses</span>
            </NavLink>
          )}

          <div className="mx-2 hidden md:flex items-center gap-2 border-l border-border pl-3" data-testid="header-user">
            <User strokeWidth={1.5} className="w-4 h-4 text-muted-foreground" />
            <div className="text-xs">
              <div className="font-heading font-semibold leading-tight">{userName}</div>
              <div className={`inline-block px-1.5 py-0 text-[9px] font-mono uppercase tracking-widest ${ROLE_COLORS[role] || "bg-muted"}`}>
                {role}
              </div>
            </div>
          </div>

          <button
            data-testid="lock-btn"
            onClick={doLock}
            className="flex items-center gap-2 px-3 lg:px-4 py-2 text-xs font-heading uppercase tracking-[0.12em] text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
          >
            <LogOut strokeWidth={1.5} className="w-4 h-4" /> <span className="hidden lg:inline">Lock</span>
          </button>
        </nav>
      </header>

      <main className="flex-1 overflow-hidden">
        <Outlet context={{ outlet }} />
      </main>
    </div>
  );
}
